import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Award, X } from "lucide-react";
import AuditReport from "./AuditReport";
import BounceState from "./BounceState";
import CertificationOverlay from "./CertificationOverlay";
import GhostButton from "./GhostButton";
import GoldButton from "./GoldButton";

export function AuditDetailModal({ open, audit, onClose, onReaudit }) {
  const { t } = useTranslation();
  const [certOpen, setCertOpen] = useState(false);
  if (!audit) return null;

  const tier = audit.tier || null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="detail-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/70 backdrop-blur-md py-10 px-4"
          onClick={onClose}
          data-testid="audit-detail-modal"
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.22, 0.9, 0.36, 1] }}
            className="relative w-full max-w-3xl rounded-3xl border border-white/10 bg-[#0a0b0f]/95 p-7 sm:p-9"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              aria-label={t("auditDetail.close")}
              className="absolute right-4 top-4 text-white/50 hover:text-white transition-colors"
              data-testid="audit-detail-close-btn"
            >
              <X className="h-5 w-5" />
            </button>

            <h2 className="font-display text-2xl text-white tracking-tight">
              {t("auditDetail.title")}
            </h2>

            <div className="mt-5 rounded-xl border border-white/8 bg-white/[0.03] p-4">
              <div className="mb-1 text-[11px] uppercase tracking-[0.18em] text-white/45">
                {t("auditDetail.originalPrompt")}
              </div>
              <pre
                className="whitespace-pre-wrap break-words text-sm text-white/85 font-sans"
                data-testid="audit-detail-prompt"
              >
                {audit.prompt}
              </pre>
            </div>

            <div className="mt-6">
              {audit.bounced ? (
                <BounceState
                  message={audit.reason_key || ""}
                  onPickCategory={() => {}}
                />
              ) : (
                <AuditReport
                  result={{ ...audit, elapsed_ms: 0 }}
                  onShowCertificate={tier ? () => setCertOpen(true) : null}
                />
              )}
            </div>

            <div className="mt-6 flex flex-wrap justify-end gap-2">
              {tier ? (
                <GhostButton
                  onClick={() => setCertOpen(true)}
                  className="gap-2"
                  data-testid="audit-detail-open-certificate-btn"
                >
                  <Award className="h-4 w-4 text-[#D4AF37]" />
                  {t("cert.openCertificate")}
                </GhostButton>
              ) : null}
              <GhostButton onClick={onClose}>{t("auditDetail.close")}</GhostButton>
              <GoldButton
                onClick={() => onReaudit?.(audit)}
                data-testid="audit-detail-reaudit-btn"
              >
                {t("auditDetail.reaudit")}
              </GoldButton>
            </div>
          </motion.div>

          {/* Nested cert overlay — reuses the same idempotent backend claim */}
          <CertificationOverlay
            open={certOpen}
            tier={tier}
            audit={audit}
            onClose={() => setCertOpen(false)}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default AuditDetailModal;
